import { NAV } from './nav';

/**
 * Route metadata for the menu, keyed by `NavItem.key`.
 *
 * This file used to also hold `NAV_ICONS`. It does not any more: a side table
 * keyed by string drifts from the table it mirrors, silently, and this one did
 * it twice — first written against 20 entries while `nav.ts` had 21, then again
 * when three Sabit Giderler entries arrived without icons. Both times the menu
 * shipped with blank rows in the collapsed rail and nothing failed. `icon` is
 * now a required field on `NavItem`, so the omission is a compile error at the
 * definition site instead of a warning nobody reads.
 *
 * `NAV_PREFETCH` stays here deliberately. It has the same drift shape, but not
 * the same cost — a missing entry loses prefetch, it does not lose the row —
 * and moving `import()` specifiers into `nav.ts` would tie the permission
 * contract to every feature module in the app. The DEV check below is what the
 * icon table used to have, pointed at the one table that can still drift.
 */

/**
 * Hover/focus prefetch for the lazy route chunks.
 *
 * Every specifier here is byte-identical to the one `App.tsx` passes to
 * `lazy()`. That is the whole trick: a dynamic `import()` is keyed by specifier
 * and module-cached, so warming it here resolves to the *same* chunk the router
 * will ask for — no duplicate chunk, no second request, and no change to
 * `App.tsx`. If a route's path is ever edited there, edit it here too or this
 * silently starts downloading a second copy.
 *
 * Calling it repeatedly is free: `import()` returns the same settled promise.
 */
export const NAV_PREFETCH: Record<string, () => Promise<unknown>> = {
  dashboard: () => import('@/features/dashboard/DashboardPage'),
  approvals: () => import('@/features/leave/ApprovalsPage'),
  'my-leave': () => import('@/features/leave/MyLeavePage'),
  'leave-records': () => import('@/features/leave/LeaveRecordsPage'),
  communication: () => import('@/features/communication/CommunicationPage'),
  messaging: () => import('@/features/messaging/MessagingPage'),

  attendance: () => import('@/features/attendance/AttendancePage'),
  'flight-premium': () => import('@/features/flight-premium/FlightPremiumPage'),
  employees: () => import('@/features/employees/EmployeesPage'),
  org: () => import('@/features/org/OrgPage'),

  guesthouse: () => import('@/features/konaklama/KonaklamaPage'),
  'guesthouse-reservations': () => import('@/features/konaklama/ReservationsPage'),
  'guesthouse-collections': () => import('@/features/konaklama/CollectionsPage'),
  'guesthouse-operations': () => import('@/features/konaklama/OperationsPage'),

  finance: () => import('@/features/finance/FinancePage'),
  expenses: () => import('@/features/finance/ExpensePage'),
  'payment-week': () => import('@/features/payment/PaymentWeekPage'),
  'payment-approval-chain': () => import('@/features/payment/ApprovalChainPage'),
  treasury: () => import('@/features/treasury/TreasuryPage'),
  'cash-flow': () => import('@/features/treasury/CashFlowPage'),
  budget: () => import('@/features/finance/BudgetPage'),

  'leased-aircraft': () => import('@/features/leased-aircraft/LeasedAircraftPage'),
  'fixed-expense': () => import('@/features/fixed-expense/FixedExpensePage'),
  'fixed-expense-commitments': () => import('@/features/fixed-expense/CommitmentsPage'),
  'fixed-expense-loans': () => import('@/features/fixed-expense/LoansPage'),

  users: () => import('@/features/users/UsersPage'),
  roles: () => import('@/features/roles/RolesPage'),
  audit: () => import('@/features/audit/AuditPage'),
  integration: () => import('@/features/integration/IntegrationPage'),
};

if (import.meta.env.DEV) {
  const unmapped = NAV.filter((item) => !NAV_PREFETCH[item.key]).map((item) => item.key);
  if (unmapped.length > 0) {
    console.warn(`[nav-meta] Prefetch tanımlanmamış menü girdileri: ${unmapped.join(', ')}`);
  }
}

/**
 * Routes that are the prefix of another route, and therefore must match
 * exactly.
 *
 * `/misafirhane` is the parent of three `/misafirhane/*` screens. Without
 * `end`, react-router marks BOTH the parent and the child active: two rows
 * highlight and a screen reader announces "current page" twice. Today that is
 * a barely visible double-grey; with a solid active fill it would be two lit
 * pills, so it has to be right.
 *
 * Derived from NAV rather than hand-listed, so the next nested route inherits
 * the fix instead of re-introducing the bug.
 */
const PREFIX_ROUTES = new Set(
  NAV.filter((item) => NAV.some((other) => other.to.startsWith(`${item.to}/`))).map(
    (item) => item.to,
  ),
);

export function isExactRoute(to: string): boolean {
  return to === '/' || PREFIX_ROUTES.has(to);
}
